import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { DocumentIcon, ArrowUpTrayIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';

const REQUIRED_DOC_VALUES = [
  'Personal Tax Return 2025',
  'Personal Tax Return 2024',
  'Personal Tax Return 2023',
  'Personal Financial Statement',
  'Resume',
  'Business Tax Return 2025',
  'Business Tax Return 2024',
  'Business Tax Return 2023',
  'Interim P&L 2026 YTD',
  'Trailing 12 Months P&L',
  'LOI',
  'Balance Sheet',
];

const EXTENSION_DOC_VALUES = ['Personal Tax Return 2022', 'Business Tax Return 2022'];
const ACQUIRING_DOC_VALUES = [
  'Acquiring Business Tax Return 2025',
  'Acquiring Business Tax Return 2024',
  'Acquiring Business Tax Return 2023',
  'Acquiring Business Interim P&L 2026 YTD',
  'Acquiring Business P&L',
  'Acquiring Business Balance Sheet',
];

const EXTRA_DOC_VALUES = [
  'CIM',
  'Debt Schedule',
  'Articles of Organization',
  'Operating Agreement',
  'AR Aging',
  'AP Aging',
  'Bank Statements',
  'Business Valuation',
  'Lease Agreement',
  'Franchise Agreement',
];

const DOC_TYPES = [
  { value: 'Personal Tax Return 2025', label: 'Personal Tax Return 2025' },
  { value: 'Personal Tax Return 2024', label: 'Personal Tax Return 2024' },
  { value: 'Personal Tax Return 2023', label: 'Personal Tax Return 2023' },
  { value: 'Personal Tax Return 2022', label: 'Personal Tax Return 2022 (only if 2025 on extension)' },
  { value: 'Personal Financial Statement', label: 'Personal Financial Statement (PFS)' },
  { value: 'Business Tax Return 2025', label: 'Business Tax Return 2025' },
  { value: 'Business Tax Return 2024', label: 'Business Tax Return 2024' },
  { value: 'Business Tax Return 2023', label: 'Business Tax Return 2023' },
  { value: 'Business Tax Return 2022', label: 'Business Tax Return 2022 (only if 2025 on extension)' },
  { value: 'Interim P&L 2026 YTD', label: 'Interim P&L 2026 (YTD)' },
  { value: 'Trailing 12 Months P&L', label: 'Trailing 12 Months P&L' },
  { value: 'LOI', label: 'Letter of Intent (LOI)' },
  { value: 'CIM', label: 'Confidential Information Memorandum (CIM)' },
  { value: 'Debt Schedule', label: 'Debt Schedule' },
  { value: 'Articles of Organization', label: 'Articles of Organization' },
  { value: 'Operating Agreement', label: 'Operating Agreement' },
  { value: 'AR Aging', label: 'AR Aging' },
  { value: 'AP Aging', label: 'AP Aging' },
  { value: 'Balance Sheet', label: 'Balance Sheet' },
  { value: 'Bank Statements', label: 'Bank Statements' },
  { value: 'Resume', label: 'Resume / CV' },
  { value: 'Business Valuation', label: 'Business Valuation' },
  { value: 'Lease Agreement', label: 'Lease Agreement' },
  { value: 'Franchise Agreement', label: 'Franchise Agreement' },
  { value: 'Acquiring Business Tax Return 2025', label: 'Acquiring Co. Tax Return 2025' },
  { value: 'Acquiring Business Tax Return 2024', label: 'Acquiring Co. Tax Return 2024' },
  { value: 'Acquiring Business Tax Return 2023', label: 'Acquiring Co. Tax Return 2023' },
  { value: 'Acquiring Business Interim P&L 2026 YTD', label: 'Acquiring Co. Interim P&L 2026 (YTD)' },
  { value: 'Acquiring Business P&L', label: 'Acquiring Co. P&L' },
  { value: 'Acquiring Business Balance Sheet', label: 'Acquiring Co. Balance Sheet' },
  { value: 'Other', label: 'Other' },
];

// Best-effort guess of doc type from filename
const guessDocType = (filename) => {
  const n = filename.toLowerCase();
  const yearMatch = n.match(/20(2[2-6])/);
  const year = yearMatch ? `20${yearMatch[1]}` : null;
  if (/p[\s_-]*&?[\s_-]*l|profit[\s_-]*and[\s_-]*loss|income[\s_-]*statement/.test(n)) {
    if (n.includes('trailing') || n.includes('ttm') || n.includes('t12')) return 'Trailing 12 Months P&L';
    if (year === '2026' || n.includes('ytd') || n.includes('interim')) return 'Interim P&L 2026 YTD';
  }
  if (/personal[\s_-]*tax|1040|individual[\s_-]*tax/.test(n) && year) return `Personal Tax Return ${year}`;
  if (/(business|biz|corporate|corp|1120|1065)[\s_-]*tax|tax[\s_-]*return/.test(n) && year) return `Business Tax Return ${year}`;
  if (/pfs|personal[\s_-]*financial[\s_-]*statement/.test(n)) return 'Personal Financial Statement';
  if (/resume|cv\b/.test(n)) return 'Resume';
  if (/loi|letter[\s_-]*of[\s_-]*intent/.test(n)) return 'LOI';
  if (/balance[\s_-]*sheet/.test(n)) return 'Balance Sheet';
  if (/cim|memorandum/.test(n)) return 'CIM';
  if (/debt[\s_-]*schedule/.test(n)) return 'Debt Schedule';
  if (/articles/.test(n)) return 'Articles of Organization';
  if (/operating[\s_-]*agreement/.test(n)) return 'Operating Agreement';
  if (/\bar[\s_-]*aging\b/.test(n)) return 'AR Aging';
  if (/\bap[\s_-]*aging\b/.test(n)) return 'AP Aging';
  if (/bank[\s_-]*statement/.test(n)) return 'Bank Statements';
  if (/valuation/.test(n)) return 'Business Valuation';
  if (/lease/.test(n)) return 'Lease Agreement';
  if (/franchise/.test(n)) return 'Franchise Agreement';
  return '';
};

export default function PendingSearcherUpload() {
  const { pendingId } = useParams();
  const [pendingData, setPendingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verified, setVerified] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [loanAppId, setLoanAppId] = useState(null); // created on first upload
  const [welcomeEmailSent, setWelcomeEmailSent] = useState(false);
  const [onExtension2025, setOnExtension2025] = useState(false);
  const [isBusinessAcquiring, setIsBusinessAcquiring] = useState(false);
  const [bulkQueue, setBulkQueue] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Try pendingSearcherSignups first
        let docSnap = await getDoc(doc(db, 'pendingSearcherSignups', pendingId));
        if (docSnap.exists()) {
          const data = docSnap.data();
          setPendingData({ id: docSnap.id, ...data, source: 'pending' });
          // Check if a loanApplication was already created for this pending searcher
          if (data.loanApplicationId) {
            setLoanAppId(data.loanApplicationId);
          } else if (data.email) {
            // Check by email
            const loanSnap = await getDocs(query(collection(db, 'loanApplications'), where('email', '==', data.email.toLowerCase().trim()), where('sharedUploadPendingId', '==', pendingId)));
            if (loanSnap.docs.length > 0) setLoanAppId(loanSnap.docs[0].id);
          }
        } else {
          // Try loanApplications directly
          docSnap = await getDoc(doc(db, 'loanApplications', pendingId));
          if (docSnap.exists()) {
            setPendingData({ id: docSnap.id, ...docSnap.data(), source: 'loan' });
            setLoanAppId(docSnap.id);
          } else {
            setError('Upload link not found.');
          }
        }

        // Fetch already uploaded files
        const docsSnap = await getDocs(query(
          collection(db, 'documentMetadata'),
          where('uploadLinkId', '==', pendingId)
        ));
        setUploadedFiles(docsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        setError('Error loading upload page.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [pendingId]);

  // Check what personal files exist
  const PERSONAL_FILE_TYPES = ['Personal Tax Return 2025', 'Personal Tax Return 2024', 'Personal Tax Return 2023', 'Personal Financial Statement'];
  const DEAL_FILE_TYPES = ['Business Tax Return 2025', 'Business Tax Return 2024', 'Business Tax Return 2023', 'LOI', 'CIM', 'Interim P&L 2026 YTD', 'Balance Sheet'];

  const checkFilesMilestone = (files) => {
    const labels = files.map(f => f.label || f.documentName);
    const hasAllPersonal = PERSONAL_FILE_TYPES.every(t => labels.includes(t));
    const hasSomePersonal = PERSONAL_FILE_TYPES.some(t => labels.includes(t));
    const hasAllDeal = ['Business Tax Return 2025', 'LOI', 'Interim P&L 2026 YTD'].every(t => labels.includes(t));
    return { hasAllPersonal, hasSomePersonal, hasAllDeal };
  };

  const sendAutoEmail = async (toEmail, firstName, type) => {
    try {
      const templates = {
        welcome: {
          subject: 'Thanks for Getting Started — Lended',
          html: `<p>Hi ${firstName},</p>
<p>Thanks for getting those documents started! Once all your personal files are uploaded (tax returns and personal financial statement), we'll be able to get you prequalified within 24 hours.</p>
<p>You can continue uploading documents here:<br><a href="https://lended.ai/upload/${pendingId}" style="display:inline-block;padding:10px 24px;background:#4f46e5;color:#ffffff;text-decoration:none;border-radius:6px;font-weight:bold;margin:8px 0;">Upload Documents</a></p>
<p>If you have any questions about what's needed, don't hesitate to reach out.</p>
<p>Best,</p>
<p>Isak<br>(914) 414-1106<br><a href="mailto:isak@lended.ai">isak@lended.ai</a> | <a href="https://lended.ai">lended.ai</a></p>`,
        },
        personalComplete: {
          subject: 'Personal Files Received — Prequalification Coming Shortly',
          html: `<p>Hi ${firstName},</p>
<p>We've received all your personal financial documents — thank you!</p>
<p>We'll have your prequalification ready shortly. Once that's done, you'll be able to start pursuing deals with your prequalification in hand.</p>
<p>Best,</p>
<p>Isak<br>(914) 414-1106<br><a href="mailto:isak@lended.ai">isak@lended.ai</a> | <a href="https://lended.ai">lended.ai</a></p>`,
        },
        allFilesComplete: {
          subject: 'All Documents Received — We\'re On It',
          html: `<p>Hi ${firstName},</p>
<p>We've received all your documents — personal files and deal files. We're working on your full analysis now.</p>
<p>We'll be in touch soon with next steps.</p>
<p>Best,</p>
<p>Isak<br>(914) 414-1106<br><a href="mailto:isak@lended.ai">isak@lended.ai</a> | <a href="https://lended.ai">lended.ai</a></p>`,
        },
      };
      const tpl = templates[type];
      if (!tpl || !toEmail) return;
      await addDoc(collection(db, 'email'), {
        to: toEmail,
        from: 'Isak @ Lended <isak@lended.ai>',
        replyTo: 'isak@lended.ai',
        subject: tpl.subject,
        message: { subject: tpl.subject, html: tpl.html },
      });
    } catch (err) { console.error('Auto email error:', err); }
  };

  // Create a loanApplication on first upload (or return existing id)
  const ensureLoanApp = async () => {
    if (loanAppId) return loanAppId;
    if (!pendingData) return null;
    const firstName = pendingData.firstName || pendingData.name?.split(' ')[0] || '';
    const lastName = pendingData.lastName || pendingData.name?.split(' ').slice(1).join(' ') || '';
    const loanRef = await addDoc(collection(db, 'loanApplications'), {
      firstName,
      lastName,
      email: pendingData.email || '',
      phoneNumber: pendingData.phoneNumber || pendingData.phone || '',
      status: 'Pre-lender',
      loanPurpose: 'Acquisition',
      businessName: '',
      createdAt: new Date(),
      updatedAt: new Date(),
      lastUserUpdate: new Date(),
      personalGuarantors: [{ firstName, lastName }],
      sharedUploadRoute: true,
      sharedUploadPendingId: pendingId,
      source: 'shared_upload',
      action: `Uploaded via shared link (pending searcher)`,
      called: false,
      allDocumentsUploaded: false,
    });
    const newId = loanRef.id;
    setLoanAppId(newId);
    if (pendingData.source === 'pending') {
      try {
        await updateDoc(doc(db, 'pendingSearcherSignups', pendingId), {
          loanApplicationId: newId,
          signedUp: true,
          signedUpAt: serverTimestamp(),
          signupMethod: 'shared_upload',
        });
      } catch (e) { console.error('Error updating pending doc:', e); }
    }
    if (!welcomeEmailSent && pendingData.email) {
      await sendAutoEmail(pendingData.email, firstName, 'welcome');
      setWelcomeEmailSent(true);
    }
    return newId;
  };

  // Uploads one file to storage, ensures a loanApp exists, saves metadata.
  // Returns saved row.
  const performUpload = (file, label, onProgress) => new Promise((resolve, reject) => {
    const storagePath = `pending-uploads/${pendingId}/${label}/${file.name}`;
    const storageRef = ref(storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, file);
    uploadTask.on('state_changed',
      (snapshot) => { if (onProgress) onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)); },
      (err) => reject(err),
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          const currentLoanAppId = await ensureLoanApp();
          const metadata = {
            documentName: label,
            label,
            fileName: file.name,
            fileUrl: downloadURL,
            uploadedAt: new Date().toISOString(),
            uploadLinkId: pendingId,
            loanApplicationId: currentLoanAppId || null,
            email: pendingData?.email || '',
            uploadedBy: 'shared_link',
            storagePath,
          };
          const docRef = await addDoc(collection(db, 'documentMetadata'), metadata);
          resolve({ id: docRef.id, ...metadata });
        } catch (err) { reject(err); }
      }
    );
  });

  // Run milestone-email checks given the current list of uploaded files
  const runMilestoneEmails = async (allFiles) => {
    const currentLoanAppId = loanAppId;
    if (!currentLoanAppId || !pendingData?.email) return;
    const { hasAllPersonal, hasAllDeal } = checkFilesMilestone(allFiles);
    const loanSnap = await getDoc(doc(db, 'loanApplications', currentLoanAppId));
    const loanData = loanSnap.exists() ? loanSnap.data() : {};
    const firstName = pendingData.firstName || pendingData.name?.split(' ')[0] || '';
    if (hasAllPersonal && !loanData.personalFilesEmailSent) {
      await sendAutoEmail(pendingData.email, firstName, 'personalComplete');
      await updateDoc(doc(db, 'loanApplications', currentLoanAppId), { personalFilesEmailSent: true, allPersonalFilesUploaded: true });
    }
    if (hasAllPersonal && hasAllDeal && !loanData.allFilesEmailSent) {
      await sendAutoEmail(pendingData.email, firstName, 'allFilesComplete');
      await updateDoc(doc(db, 'loanApplications', currentLoanAppId), { allFilesEmailSent: true, allDocumentsUploaded: true });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    const label = (customLabel && customLabel.trim()) || (selectedDocType === 'Other' ? '' : selectedDocType);
    if (!label) return;
    setUploading(true);
    setUploadProgress(0);
    setSuccessMessage('');
    setError('');
    try {
      const saved = await performUpload(selectedFile, label, setUploadProgress);
      const newFiles = [...uploadedFiles, saved];
      setUploadedFiles(newFiles);
      setSuccessMessage(`${label} uploaded successfully!`);
      setSelectedFile(null);
      setSelectedDocType('');
      setCustomLabel('');
      const fileInput = document.getElementById('file-upload-input');
      if (fileInput) fileInput.value = '';
      await runMilestoneEmails(newFiles);
    } catch (err) {
      setError('Upload failed: ' + (err.message || 'unknown error'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ---- Bulk upload ----
  const handleBulkFilesPicked = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setBulkQueue(Array.from(fileList).map(file => ({
      file,
      label: guessDocType(file.name),
      customLabel: '',
      status: 'pending',
      error: '',
    })));
    setError(null);
    setSuccessMessage('');
  };

  const updateBulkRow = (idx, patch) => {
    setBulkQueue(prev => prev.map((row, i) => i === idx ? { ...row, ...patch } : row));
  };

  const removeBulkRow = (idx) => {
    setBulkQueue(prev => prev.filter((_, i) => i !== idx));
  };

  const startBulkUpload = async () => {
    setBulkUploading(true);
    setError(null);
    const acc = [...uploadedFiles];
    for (let i = 0; i < bulkQueue.length; i++) {
      const row = bulkQueue[i];
      if (row.status === 'done') continue;
      const label = (row.customLabel && row.customLabel.trim())
        || (row.label === 'Other' ? '' : row.label);
      if (!label) { updateBulkRow(i, { status: 'error', error: 'Pick a doc type or give it a custom name' }); continue; }
      updateBulkRow(i, { status: 'uploading', error: '' });
      try {
        const saved = await performUpload(row.file, label);
        acc.push(saved);
        setUploadedFiles([...acc]);
        updateBulkRow(i, { status: 'done' });
      } catch (err) {
        updateBulkRow(i, { status: 'error', error: err.message || 'Upload failed' });
      }
    }
    await runMilestoneEmails(acc);
    setBulkUploading(false);
  };

  const closeBulkModal = () => {
    if (bulkUploading) return;
    setBulkQueue([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error && !pendingData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
          <XMarkIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Link Not Found</h2>
          <p className="text-gray-500">This upload link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  const name = pendingData?.firstName
    ? `${pendingData.firstName} ${pendingData.lastName || ''}`
    : pendingData?.name || 'Applicant';

  const uploadedLabelsList = uploadedFiles.map(f => f.label || f.documentName);
  const activeRequiredDocs = [
    ...REQUIRED_DOC_VALUES,
    ...(onExtension2025 ? EXTENSION_DOC_VALUES : []),
    ...(isBusinessAcquiring ? ACQUIRING_DOC_VALUES : []),
  ];
  const docLabelLookup = Object.fromEntries(DOC_TYPES.map(d => [d.value, d.label]));
  const requiredUploadedCount = activeRequiredDocs.filter(v => uploadedLabelsList.includes(v)).length;
  const allRequiredDone = requiredUploadedCount === activeRequiredDocs.length;
  const extrasUploadedCount = EXTRA_DOC_VALUES.filter(v => uploadedLabelsList.includes(v)).length;

  const handleVerifyEmail = () => {
    const expected = (pendingData?.email || '').toLowerCase().trim();
    const entered = emailInput.toLowerCase().trim();
    if (!entered) { setEmailError('Please enter your email.'); return; }
    if (expected && entered !== expected) { setEmailError('Email does not match our records. Please try again.'); return; }
    setVerified(true);
    setEmailError('');
  };

  // Show email verification gate first
  if (!verified) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-8 px-4">
        <div className="max-w-md mx-auto mt-16">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-indigo-100 mb-4">
              <ArrowUpTrayIcon className="h-8 w-8 text-indigo-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Verify Your Email</h1>
            <p className="text-gray-500 mt-1">Enter your email to access your document upload page.</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input
              type="email"
              value={emailInput}
              onChange={(e) => { setEmailInput(e.target.value); setEmailError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleVerifyEmail()}
              placeholder="you@example.com"
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 mb-3"
            />
            {emailError && <p className="text-sm text-red-600 mb-3">{emailError}</p>}
            <button
              onClick={handleVerifyEmail}
              className="w-full py-3 px-4 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors"
            >
              Continue
            </button>
          </div>
          <div className="text-center mt-6 text-xs text-gray-400">
            <p>Powered by <a href="https://lended.ai" className="text-indigo-500">Lended</a></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-indigo-100 mb-4">
            <ArrowUpTrayIcon className="h-8 w-8 text-indigo-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Upload Documents</h1>
          <p className="text-gray-500 mt-1">Hi {name.split(' ')[0]}, upload your documents below to get started with Lended.</p>
        </div>

        {/* Progress checklist — mirrors the broker full-deal upload page */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          {/* Required for Term Sheet */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Required for Term Sheet</h3>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                allRequiredDone ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
              }`}>
                {requiredUploadedCount} / {activeRequiredDocs.length}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {activeRequiredDocs.map(value => {
                const uploaded = uploadedLabelsList.includes(value);
                return (
                  <div key={value} className="flex items-center gap-2 text-xs">
                    {uploaded ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <div className="h-4 w-4 rounded-full border-2 border-orange-300 flex-shrink-0" />
                    )}
                    <span className={uploaded ? 'text-green-700' : 'text-gray-600'}>
                      {docLabelLookup[value] || value}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Extra Files */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-sm font-semibold text-gray-700">Extra Files</h3>
              {extrasUploadedCount > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700">
                  {extrasUploadedCount} uploaded
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {EXTRA_DOC_VALUES.map(value => {
                const uploaded = uploadedLabelsList.includes(value);
                return (
                  <div key={value} className="flex items-center gap-2 text-xs">
                    {uploaded ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <div className="h-4 w-4 rounded-full border-2 border-gray-200 flex-shrink-0" />
                    )}
                    <span className={uploaded ? 'text-green-700' : 'text-gray-400'}>
                      {docLabelLookup[value] || value}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bulk upload CTA */}
        <div className="bg-white rounded-xl shadow-lg p-5 mb-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900">Have a bunch of files?</h3>
            <p className="text-xs text-gray-500 mt-0.5">Upload multiple files or a whole folder — we'll tag each one for you.</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <label className="cursor-pointer text-xs font-medium px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">
              Upload files
              <input type="file" multiple className="hidden"
                onChange={(e) => { handleBulkFilesPicked(e.target.files); e.target.value = ''; }} />
            </label>
            <label className="cursor-pointer text-xs font-medium px-3 py-2 rounded-lg bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50">
              Upload folder
              <input type="file" multiple webkitdirectory="" directory="" className="hidden"
                onChange={(e) => { handleBulkFilesPicked(e.target.files); e.target.value = ''; }} />
            </label>
          </div>
        </div>

        {/* Deal options */}
        <div className="bg-white rounded-xl shadow-lg p-5 mb-6 space-y-3">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={onExtension2025}
              onChange={(e) => setOnExtension2025(e.target.checked)}
              className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">
              <span className="font-medium">My 2025 returns are on extension</span>
              <span className="block text-xs text-gray-500 mt-0.5">If checked, we'll also need 2022 personal and business tax returns.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isBusinessAcquiring}
              onChange={(e) => setIsBusinessAcquiring(e.target.checked)}
              className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">
              <span className="font-medium">A business is acquiring another business</span>
              <span className="block text-xs text-gray-500 mt-0.5">If checked, we'll also need the acquiring company's 2023–2025 returns, 2026 YTD interim, P&amp;L, and balance sheet.</span>
            </span>
          </label>
        </div>

        {/* Upload Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload a Document</h2>

          {/* Document Type Selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
            <select
              value={selectedDocType}
              onChange={(e) => setSelectedDocType(e.target.value)}
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="">Select document type...</option>
              {DOC_TYPES.map(dt => (
                <option key={dt.value} value={dt.value}>{dt.label}</option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {selectedDocType === 'Other' ? 'Describe the document *' : 'Or use a custom name (overrides selection)'}
            </label>
            <input
              type="text"
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              placeholder="e.g., Franchise Agreement"
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          {/* File Input */}
          <div className="mb-4">
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                selectedFile ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50'
              }`}
              onClick={() => document.getElementById('file-upload-input')?.click()}
            >
              <input
                id="file-upload-input"
                type="file"
                className="hidden"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.tiff,.tif"
              />
              {selectedFile ? (
                <>
                  <DocumentIcon className="mx-auto h-10 w-10 text-green-500" />
                  <p className="mt-2 text-sm font-medium text-green-700">{selectedFile.name}</p>
                  <p className="text-xs text-green-500">Click to change file</p>
                </>
              ) : (
                <>
                  <ArrowUpTrayIcon className="mx-auto h-10 w-10 text-gray-400" />
                  <p className="mt-2 text-sm font-medium text-gray-700">Drop file here or click to browse</p>
                  <p className="text-xs text-gray-400 mt-1">PDF, DOC, XLS, CSV, JPG, PNG</p>
                </>
              )}
            </div>
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div className="bg-indigo-600 h-2.5 rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
              </div>
            </div>
          )}

          {/* Error */}
          {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

          {/* Success */}
          {successMessage && (
            <div className="mb-4 rounded-lg bg-green-50 p-3 text-sm text-green-700 flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5" />{successMessage}
            </div>
          )}

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading || (!selectedDocType && !customLabel.trim()) || (selectedDocType === 'Other' && !customLabel.trim())}
            className="w-full py-3 px-4 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {uploading ? 'Uploading...' : 'Upload Document'}
          </button>
        </div>

        {/* Uploaded Files List */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Uploaded Documents ({uploadedFiles.length})</h2>
          {uploadedFiles.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No documents uploaded yet.</p>
          ) : (
            <div className="space-y-2">
              {uploadedFiles.map((file, idx) => (
                <div key={file.id || idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <DocumentIcon className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{file.label || file.documentName}</p>
                      <p className="text-xs text-gray-400">{file.fileName} — {file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString() : ''}</p>
                    </div>
                  </div>
                  {file.fileUrl && (
                    <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">View</a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-xs text-gray-400">
          <p>Powered by <a href="https://lended.ai" className="text-indigo-500">Lended</a></p>
          <p className="mt-1">Questions? Email <a href="mailto:isak@lended.ai" className="text-indigo-500">isak@lended.ai</a> or call (914) 414-1106</p>
        </div>
      </div>

      {/* Bulk upload modal */}
      {bulkQueue.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Tag {bulkQueue.length} {bulkQueue.length === 1 ? 'file' : 'files'}</h3>
                <p className="text-xs text-gray-500 mt-0.5">We tried to guess each doc type from the filename — fix any that look wrong.</p>
              </div>
              <button onClick={closeBulkModal} disabled={bulkUploading} className="text-gray-400 hover:text-gray-600 disabled:opacity-30">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {bulkQueue.map((row, idx) => (
                <div key={idx} className={`border rounded-lg p-3 ${
                  row.status === 'done' ? 'border-green-200 bg-green-50' :
                  row.status === 'error' ? 'border-red-200 bg-red-50' :
                  row.status === 'uploading' ? 'border-indigo-200 bg-indigo-50' :
                  'border-gray-200'
                }`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <DocumentIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-800 truncate">{row.file.name}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">{(row.file.size / 1024).toFixed(0)} KB</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {row.status === 'done' && <CheckCircleIcon className="h-5 w-5 text-green-500" />}
                      {row.status === 'uploading' && <span className="text-xs text-indigo-600 animate-pulse">Uploading…</span>}
                      {row.status !== 'done' && row.status !== 'uploading' && (
                        <button onClick={() => removeBulkRow(idx)} disabled={bulkUploading} className="text-gray-400 hover:text-red-500 disabled:opacity-30">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <select
                    value={row.label}
                    onChange={(e) => updateBulkRow(idx, { label: e.target.value })}
                    disabled={bulkUploading || row.status === 'done' || !!row.customLabel}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm disabled:bg-gray-100"
                  >
                    <option value="">Select document type…</option>
                    {DOC_TYPES.map(dt => (
                      <option key={dt.value} value={dt.value}>{dt.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={row.customLabel}
                    onChange={(e) => updateBulkRow(idx, { customLabel: e.target.value })}
                    placeholder="…or type a custom name (overrides above)"
                    disabled={bulkUploading || row.status === 'done'}
                    className="mt-2 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm disabled:bg-gray-100"
                  />
                  {row.error && <p className="mt-2 text-xs text-red-600">{row.error}</p>}
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t flex items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                {bulkQueue.filter(r => r.status === 'done').length} of {bulkQueue.length} uploaded
              </p>
              <div className="flex gap-2">
                <button onClick={closeBulkModal} disabled={bulkUploading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                  {bulkQueue.every(r => r.status === 'done') ? 'Done' : 'Cancel'}
                </button>
                <button onClick={startBulkUpload}
                  disabled={bulkUploading || bulkQueue.every(r => r.status === 'done') || bulkQueue.some(r => !r.label && r.status !== 'done')}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                  {bulkUploading ? 'Uploading…' : 'Upload all'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
